// Modèle de données d'Ordopli. Les noms suivent les colonnes de la base (snake_case).

/** Rôles des comptes (accès au site). « chef » = chef d'atelier. */
export type Role = "admin" | "production" | "chef" | "saisie" | "conducteur" | "direction";

export const ROLE_LABEL: Record<Role, string> = {
  admin: "Administrateur",
  production: "Chef de production",
  chef: "Chef d'atelier",
  saisie: "Saisie des jobs",
  conducteur: "Conducteur",
  direction: "Direction (lecture)",
};

/** Ce qu'un rôle peut modifier. Tout le monde peut consulter. */
export type Permission = "planning" | "jobs" | "machines" | "equipe";

export const PERMISSION_LABEL: Record<Permission, { label: string; hint: string }> = {
  planning: { label: "Planning", hint: "Optimiser, déplacer, publier, attribuer les conducteurs" },
  jobs: { label: "Jobs", hint: "Créer, modifier, annuler des jobs" },
  machines: { label: "Machines et paramètres", hint: "Machines, horaires, conditionnements, aléas" },
  equipe: { label: "Opérateurs et tablettes", hint: "Opérateurs, codes, rôles, tablettes des postes" },
};

export const DEFAULT_PERMISSIONS: Record<Exclude<Role, "admin">, Permission[]> = {
  production: ["planning", "jobs", "machines", "equipe"],
  chef: ["planning", "jobs", "machines", "equipe"],
  saisie: ["jobs"],
  conducteur: [],
  direction: [],
};

/** Rôles des opérateurs sur la tablette. */
export type OperatorRole = "conducteur" | "chef_atelier" | "chef_production";
export const OPERATOR_ROLE_LABEL: Record<OperatorRole, string> = {
  conducteur: "Conducteur",
  chef_atelier: "Chef d'atelier",
  chef_production: "Chef de production",
};

export type Operation = "pli" | "encartage" | "piquage" | "encartage_piquage" | "dos_carre_colle" | "rainage" | "rainage_pliage" | "pelliculage";

export const OPERATION_LABEL: Record<Operation, string> = {
  pli: "Pliage",
  encartage: "Encartage",
  piquage: "Piquage",
  encartage_piquage: "Encartage-piquage",
  dos_carre_colle: "Dos carré collé",
  rainage: "Rainage",
  rainage_pliage: "Rainage-pliage",
  pelliculage: "Pelliculage",
};

export const FOLD_TYPES = ["simple", "croisé", "roulé", "accordéon", "fenêtre", "double parallèle"] as const;
export const FORMATS = ["SRA3", "A3", "A4", "A5", "A6", "DL", "21x21", "Carré 15"] as const;

export type MachineType = "plieuse" | "encarteuse_piqueuse" | "piqueuse" | "dos_carre_colle" | "raineur_plieur" | "pelliculeuse" | "autre";

export const MACHINE_TYPE_LABEL: Record<MachineType, string> = {
  plieuse: "Plieuse",
  encarteuse_piqueuse: "Encarteuse-piqueuse",
  piqueuse: "Piqueuse",
  dos_carre_colle: "Dos carré collé",
  raineur_plieur: "Raineur-plieur",
  pelliculeuse: "Pelliculeuse",
  autre: "Autre",
};

export interface Organization {
  id: string;
  name: string;
  siret: string | null;
  timezone: string;
  /** Horaires d'ouverture : jours ISO (1 = lundi) et plages "HH:MM". */
  hours: WorkingHours;
  /** Droits de chaque rôle (l'administrateur a toujours tout). */
  permissions: Partial<Record<Role, Permission[]>>;
  created_at: string;
  /** Rôle de l'utilisateur connecté dans cette imprimerie (ajouté au chargement). */
  my_role?: Role;
}

export interface WorkingHours {
  days: number[];
  ranges: { start: string; end: string }[];
}

export const DEFAULT_HOURS: WorkingHours = {
  days: [1, 2, 3, 4, 5],
  ranges: [
    { start: "08:00", end: "12:00" },
    { start: "13:00", end: "17:00" },
  ],
};

export interface Machine {
  id: string;
  org_id: string;
  name: string;
  type: MachineType;
  operations: Operation[];
  /** Formats d'entrée acceptés. Vide = tous. */
  formats: string[];
  /** Feuilles (ou exemplaires) par heure en production. */
  speed_per_hour: number;
  /** Temps de calage par défaut quand le réglage change, en minutes. */
  setup_min: number;
  position: number;
  active: boolean;
  /** Caractéristiques techniques, selon le type (voir catalog.ts). */
  specs: Record<string, ParamValue>;
}

export type ParamValue = string | number | boolean | string[] | null;

export interface Operator {
  id: string;
  org_id: string;
  name: string;
  role: OperatorRole;
  machine_ids: string[];
  active: boolean;
}

export type JobStatus = "a_planifier" | "planifie" | "en_cours" | "termine" | "annule";

export interface Job {
  id: string;
  org_id: string;
  of_number: string;
  client: string;
  title: string;
  quantity: number;
  due_at: string;
  priority: "normal" | "urgent";
  sheets_ready_at: string | null;
  notes: string;
  status: JobStatus;
  created_at: string;
  updated_at: string;
}

export type TaskStatus = "a_planifier" | "planifie" | "calage" | "production" | "pause" | "termine";

export interface Task {
  id: string;
  org_id: string;
  job_id: string;
  seq: number;
  operation: Operation;
  fold_type: string | null;
  format_in: string;
  format_out: string;
  paper: string;
  grammage: number | null;
  pages: number | null;
  /** Réglages techniques propres à l'opération (agrafes, colle, film…). */
  params: Record<string, ParamValue>;
  /** Conditionnement choisi dans la liste de l'imprimerie. */
  packaging: string | null;
  /** Conducteur attribué (facultatif). */
  operator_id: string | null;
  // planning (brouillon du chef d'atelier)
  machine_id: string | null;
  planned_start: string | null;
  planned_end: string | null;
  setup_min: number | null;
  run_min: number | null;
  /** Durée de production fixée à la main par le chef d'atelier (minutes). Vide = calculée. */
  manual_run_min?: number | null;
  locked: boolean;
  // planning publié (ce que voient les tablettes)
  pub_machine_id: string | null;
  pub_start: string | null;
  // réalisé
  status: TaskStatus;
  produced_qty: number;
  waste_qty: number;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
}

/** Nature de l'aléa : libellé libre, choisi dans la liste du poste. */
export type IncidentKind = string;

const LEGACY_INCIDENT: Record<string, string> = {
  panne: "Panne machine", bourrage: "Bourrage", attente_feuilles: "Attente feuilles", autre: "Autre",
};
export function incidentLabel(kind: string): string { return LEGACY_INCIDENT[kind] || kind; }

export interface Incident {
  id: string;
  org_id: string;
  machine_id: string;
  task_id: string | null;
  operator_id: string | null;
  kind: IncidentKind;
  comment: string;
  started_at: string;
  ended_at: string | null;
}

export type TaskEventKind = "calage" | "production" | "pause" | "reprise" | "termine" | "quantite";

export interface TaskEvent {
  id: string;
  org_id: string;
  task_id: string;
  machine_id: string;
  operator_id: string | null;
  kind: TaskEventKind;
  qty: number | null;
  at: string;
}

export interface Device {
  id: string;
  org_id: string;
  machine_id: string;
  name: string;
  created_at: string;
  last_seen_at: string | null;
}

export interface Member {
  user_id: string;
  org_id: string;
  role: Role;
  email: string;
  name: string;
}

/** Une étape de job avec son job parent, pour l'affichage. */
export interface TaskWithJob extends Task {
  job: Job;
}

/** Ce que reçoit une tablette associée à une machine. */
export interface DeviceState {
  org_name: string;
  machine: Machine;
  operators: { id: string; name: string; role: OperatorRole }[];
  current: TaskWithJob | null;
  next: TaskWithJob[];
  /** Planning du poste : terminés aujourd'hui, en cours, puis la suite publiée. */
  day: TaskWithJob[];
  incident: Incident | null;
  /** Aléas personnalisés : liste du type de machine (vide = liste par défaut) et ajouts propres au poste. */
  aleas_type: string[];
  aleas_machine: string[];
  /** Horaires de l'atelier, pour dessiner la frise du poste. */
  hours: WorkingHours;
  server_time: string;
}

export type DeviceAction =
  | { type: "calage"; task_id: string }
  | { type: "production"; task_id: string }
  | { type: "pause"; task_id: string }
  | { type: "reprise"; task_id: string }
  | { type: "quantite"; task_id: string; qty: number }
  | { type: "termine"; task_id: string; qty: number; waste: number }
  | { type: "alea"; task_id: string | null; kind: IncidentKind; comment: string }
  | { type: "note_alea"; comment: string }
  | { type: "fin_alea" };

export interface Invitation {
  org_id: string;
  email: string;
  role: Role;
  created_at: string;
}

/** Listes propres à l'imprimerie : conditionnements et aléas. */
export type CatalogKind = "conditionnement" | "alea";
export interface CatalogItem {
  id: string;
  org_id: string;
  kind: CatalogKind;
  label: string;
  /** Type de machine concerné ('' = tous). */
  scope: string;
  /** Machine précise (aléas propres à un poste). */
  machine_id: string | null;
  position: number;
}

// ---------------------------------------------------------------------------
// Documents du job (BAT, maquette, photo du produit fini)
// ---------------------------------------------------------------------------
export type DocKind = "bat" | "maquette" | "photo" | "autre";
export const DOC_KIND_LABEL: Record<DocKind, string> = { bat: "BAT", maquette: "Maquette", photo: "Photo du produit fini", autre: "Autre document" };

export interface JobDocument {
  id: string;
  job_id: string;
  name: string;
  kind: DocKind;
  /** Nombre de pages (images) du document. */
  pages: number;
  /** Petite image de la première page (data URL). */
  thumb: string;
  created_at: string;
}

/** Une page prête à afficher : image JPEG en data URL. */
export interface DocPage { data: string; width: number; height: number }
